import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ExternalLink,
  LogOut,
  Map,
  Menu,
  Phone,
  Route,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/ChaloAIIcon';
import { useLanguage } from '@/hooks/useLanguage';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/routes/routePaths';
import { useAuth } from '@/components/AuthProvider';
import { clearAuthSession } from '@/utils/authStorage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getHeaderNavSections,
  type FooterNavLink,
  type FooterNavSection,
} from '@/components/landing/footerNavConfig';
import { cn } from '@/lib/utils';

interface HeaderProps {
  onAuthClick: (mode: 'login' | 'register') => void;
  selectedCountry: string;
  onCountryChange: (country: string) => void;
}

const SECTION_BLURB: Record<string, string> = {
  company: 'About Duri and our mission',
  guests: 'Everything you need as a guest',
  hosts: 'Everything you need to host rides',
  commute: 'Office and weekday carpool options',
};

function pathOf(href: string) {
  return href.split('?')[0];
}

function NavLinkRow({
  link,
  onNavigate,
  dense = false,
  className: classNameProp,
  ...props
}: {
  link: FooterNavLink;
  onNavigate?: () => void;
  dense?: boolean;
  className?: string;
} & React.ComponentPropsWithoutRef<'a'>) {
  const Icon = link.Icon;
  const className = cn(
    'group flex w-full items-center gap-3 rounded-xl text-left outline-none transition-colors',
    dense ? 'px-2 py-2' : 'px-2.5 py-2.5',
    'hover:bg-brand-soft focus-visible:bg-brand-soft',
    classNameProp
  );

  const content = (
    <>
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand transition-colors',
          'group-hover:bg-brand group-hover:text-white',
          dense ? 'h-8 w-8' : 'h-9 w-9'
        )}
      >
        {Icon ? <Icon className={dense ? 'h-4 w-4' : 'h-[17px] w-[17px]'} aria-hidden /> : null}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium text-gray-800 group-hover:text-brand-dark">
        {link.name}
      </span>
      {link.external ? (
        <ExternalLink
          className="h-3.5 w-3.5 shrink-0 text-gray-300 group-hover:text-brand"
          aria-hidden
        />
      ) : null}
    </>
  );

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onNavigate}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={link.href} className={className} onClick={onNavigate} {...props}>
      {content}
    </Link>
  );
}

function DesktopNavDropdown({ section }: { section: FooterNavSection }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const SectionIcon = section.Icon;
  const isActive = section.links.some(
    (link) => !link.external && location.pathname === pathOf(link.href)
  );

  // Ensure menu closes after SPA navigation (incl. same-route clicks).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const closeMenu = () => setOpen(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={cn(
          'group flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold outline-none transition-all',
          'hover:bg-brand-soft hover:text-brand-dark',
          'data-[state=open]:bg-brand-soft data-[state=open]:text-brand-dark',
          isActive ? 'bg-brand-soft text-brand-dark' : 'text-gray-800'
        )}
      >
        {SectionIcon ? <SectionIcon className="h-4 w-4 shrink-0" aria-hidden /> : null}
        <span>{section.title}</span>
        <ChevronDown
          className="h-3.5 w-3.5 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180"
          aria-hidden
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={12}
        className="w-[20.5rem] overflow-hidden rounded-2xl border border-gray-100 bg-white p-0 shadow-xl shadow-gray-200/60"
      >
        <div className="flex items-center gap-3 border-b border-gray-100 bg-brand-soft/70 px-4 py-3.5">
          {SectionIcon ? (
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand shadow-sm">
              <SectionIcon className="h-5 w-5" aria-hidden />
            </span>
          ) : null}
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">{section.title}</p>
            <p className="text-xs text-gray-500">
              {SECTION_BLURB[section.id] ?? ''}
            </p>
          </div>
        </div>
        <div className="grid max-h-[70vh] gap-0.5 overflow-y-auto p-2">
          {section.links.map((link) => (
            <DropdownMenuItem
              key={`${section.id}-${link.name}`}
              asChild
              className="cursor-pointer rounded-xl p-0 focus:bg-transparent"
              onSelect={closeMenu}
            >
              <NavLinkRow link={link} onNavigate={closeMenu} />
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNavSection({
  section,
  openId,
  onToggle,
  onNavigate,
}: {
  section: FooterNavSection;
  openId: string | null;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}) {
  const isOpen = openId === section.id;
  const SectionIcon = section.Icon;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
          isOpen ? 'bg-brand-soft' : 'hover:bg-gray-50'
        )}
        aria-expanded={isOpen}
        onClick={() => onToggle(section.id)}
      >
        {SectionIcon ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
            <SectionIcon className="h-4 w-4" aria-hidden />
          </span>
        ) : null}
        <span className="flex-1 text-sm font-semibold text-gray-900">{section.title}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform duration-200',
            isOpen && 'rotate-180 text-brand'
          )}
          aria-hidden
        />
      </button>
      {isOpen && (
        <ul className="space-y-0.5 border-t border-gray-100 bg-gray-50/60 p-2">
          {section.links.map((link) => (
            <li key={`${section.id}-${link.name}`}>
              <NavLinkRow link={link} onNavigate={onNavigate} dense />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Header: React.FC<HeaderProps> = ({ onAuthClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState<string | null>(null);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setAuthenticated } = useAuth();

  const headerSections = useMemo(() => getHeaderNavSections(t), [t]);

  const handleLogout = () => {
    clearAuthSession();
    setAuthenticated(false);
    navigate(ROUTE_PATHS.MARKETING_LANDING);
    setIsMobileMenuOpen(false);
  };

  const goToBrandHome = () => {
    const brandHome = ROUTE_PATHS.MARKETING_LANDING;
    if (location.pathname === brandHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(brandHome);
    }
  };

  const isActivePath = (path: string) => location.pathname === path;

  const desktopLinkClass = (path: string) =>
    cn(
      'flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold transition-colors hover:bg-brand-soft hover:text-brand-dark',
      isActivePath(path) ? 'bg-brand-soft text-brand-dark' : 'text-gray-800'
    );

  const mobileLinkClass = (path: string) =>
    cn(
      'flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-3 text-sm font-semibold transition-colors hover:bg-brand-soft hover:text-brand-dark',
      isActivePath(path)
        ? 'border-brand-border bg-brand-soft text-brand-dark'
        : 'text-gray-800'
    );

  const closeMobile = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileSection(null);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/95 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between gap-3">
          <div className="flex items-center">
            <button
              type="button"
              onClick={goToBrandHome}
              className="flex items-center focus:outline-none"
              aria-label="Go to home"
            >
              <Logo size={58} />
            </button>
          </div>

          <nav className="hidden items-center gap-0.5 lg:flex xl:gap-1" aria-label="Primary">
            {headerSections.map((section) => (
              <DesktopNavDropdown key={section.id} section={section} />
            ))}
            <Link
              to={ROUTE_PATHS.POPULAR_ROUTES}
              aria-current={isActivePath(ROUTE_PATHS.POPULAR_ROUTES) ? 'page' : undefined}
              className={desktopLinkClass(ROUTE_PATHS.POPULAR_ROUTES)}
            >
              <Map className="h-4 w-4" />
              <span>{t('popularRoutes', 'header')}</span>
            </Link>
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <Button
              onClick={() => navigate(ROUTE_PATHS.TRACK_RIDE)}
              className="flex items-center gap-2 rounded-xl bg-brand px-4 text-white hover:bg-brand-dark"
            >
              <Route className="h-4 w-4" />
              <span>Manage Ride</span>
            </Button>
            {isAuthenticated ? (
              <Button
                variant="outline"
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-xl border-brand text-brand hover:bg-brand-soft hover:text-brand-dark"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('logout', 'header')}</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => onAuthClick('login')}
                className="hidden items-center gap-2 rounded-xl border-brand text-brand hover:bg-brand-soft xl:flex"
              >
                <Phone className="h-4 w-4" />
                <span>{t('login', 'header')}</span>
              </Button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className="rounded-lg p-2 hover:bg-brand-soft lg:hidden"
            aria-expanded={isMobileMenuOpen}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="border-t border-gray-100 py-4 lg:hidden">
            <nav className="flex flex-col gap-2.5" aria-label="Mobile">
              {headerSections.map((section) => (
                <MobileNavSection
                  key={section.id}
                  section={section}
                  openId={openMobileSection}
                  onToggle={(id) =>
                    setOpenMobileSection((current) => (current === id ? null : id))
                  }
                  onNavigate={closeMobile}
                />
              ))}

              <Link
                to={ROUTE_PATHS.POPULAR_ROUTES}
                onClick={closeMobile}
                aria-current={isActivePath(ROUTE_PATHS.POPULAR_ROUTES) ? 'page' : undefined}
                className={mobileLinkClass(ROUTE_PATHS.POPULAR_ROUTES)}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-soft text-brand">
                  <Map className="h-4 w-4" />
                </span>
                <span>{t('popularRoutes', 'header')}</span>
              </Link>

              <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                <Button
                  onClick={() => {
                    closeMobile();
                    navigate(ROUTE_PATHS.TRACK_RIDE);
                  }}
                  className="w-full rounded-xl bg-brand text-white hover:bg-brand-dark"
                >
                  <Route className="mr-2 h-4 w-4" />
                  Manage Ride
                </Button>
                {isAuthenticated ? (
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    className="w-full rounded-xl border-brand text-brand hover:bg-brand-soft"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('logout', 'header')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => onAuthClick('login')}
                    className="w-full rounded-xl border-brand text-brand hover:bg-brand-soft"
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    {t('login', 'header')}
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
